import { CanActivateFn, Route, Routes } from '@angular/router';
import {
  ACCOUNT_OVERVIEW,
  accountOverviewResolver,
  agentInContextGuard,
  agentReportsSummaryGuard,
  API_AUTH_STATUS,
  apiAuthResolver,
  apiTokenGuard,
  APP_SESSION_DATA,
  AppConfigModel,
  applicationSessionResolver,
  authGuard,
  browserSupportGuard,
  canDeactivateGuard,
  consumerAccountSummaryGuard,
  ENROLLMENT_TRANSACTION_ID,
  enrollmentGuard,
  enrollmentTransactionResolver,
  genericFormTransactionResolver,
  GOOGLE_TAG_MANAGER,
  googleTagManagerResolver,
  hraFormTransactionResolver,
  LIVE_EDIT_ROOT,
  LOGIN_STATUS,
  loginStatusResolver,
  medicareGroupGuard,
  Page,
  PageTags,
  QUOTE_FETCH,
  quoteResolver,
  RETIRE_FLO_SURVEY,
  retireFloSurveyResolver,
  RouteGeneratorOptions,
  SET_SHARED_RESOURCES,
  SET_SITE_TYPE,
  sharedResourcesResolver,
  siteTypeResolver,
  standaloneFormTransactionResolver,
  widgetValidationGuard
} from 'vnext-shared';
import { environment } from '../../../environments/environment';
import { LiveEditRootComponent } from '../live-edit/live-edit-root/live-edit-root.component';
import { TemplateLookupService } from '../services/template.lookup.service';
import { caseInsensitiveMatcher } from './case-insensitive.matcher';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class RouteBuilder {
  /**
   * Returns an array of activation guards given a page.
   */
  private static getActivationGuards(page: Page): CanActivateFn[] {
    const guards: CanActivateFn[] = [];
    if (!environment.liveedit) {
      guards.push(browserSupportGuard);
      // All routes should have AuthGuard activation present.
      guards.push(authGuard);
    }
    const pageTags = page.tags;
    if (pageTags?.length) {
      if (pageTags.includes(PageTags.CONSUMER_ACCOUNT_SUMMARY)) {
        guards.push(consumerAccountSummaryGuard);
      }
      if (pageTags.includes(PageTags.REPORTS_SUMMARY)) {
        guards.push(agentReportsSummaryGuard);
      }
      if (pageTags.includes(PageTags.HOME) || pageTags.includes(PageTags.PLAN_LIST)) {
        guards.push(medicareGroupGuard);
      }
      if (!pageTags.includes(PageTags.PAGE_NOT_FOUND)) {
        guards.push(agentInContextGuard);
      }
    }

    return guards;
  }

  // TODO: use the param or environment var? update across the board accordingly
  static build(config: AppConfigModel, options?: RouteGeneratorOptions): Routes {
    let routes: Routes = [];
    if (config.pages) {
      routes = config.pages
        .filter(page => !page.disabled)
        .filter(page => page?.tags?.find(tag => !options?.disabledRoutes.includes(tag as PageTags)))
        .reduce<Routes>((pages, page) => {
          if (page.pageName) {
            if (page?.tags && this.includesFormPageTag(page.tags) && config?.homepage !== page.pageName) {
              const pageTemplate = TemplateLookupService.getTemplate(page.template);
              if (pageTemplate) {
                pages.push(
                  {
                    matcher: caseInsensitiveMatcher(page.pageName),
                    component: pageTemplate,
                    pathMatch: 'full',
                    data: { title: page.title },
                    canDeactivate: [apiTokenGuard],
                    canActivate: [authGuard, enrollmentGuard, agentInContextGuard],
                    resolve: {
                      [APP_SESSION_DATA]: applicationSessionResolver,
                      [ENROLLMENT_TRANSACTION_ID]: this.getFormResolver(page.tags),
                      [LOGIN_STATUS]: loginStatusResolver,
                      [SET_SHARED_RESOURCES]: sharedResourcesResolver,
                      [QUOTE_FETCH]: quoteResolver
                    }
                  },
                  {
                    matcher: caseInsensitiveMatcher(`${page.pageName}/:pageName`),
                    component: pageTemplate,
                    pathMatch: 'full',
                    // TODO: eventually account for uri param in page title
                    canDeactivate: [apiTokenGuard],
                    canActivate: [authGuard, enrollmentGuard, agentInContextGuard],
                    data: {
                      title: page.title,
                      reuseComponent:
                        page.tags.includes(PageTags.ENROLLMENT) ||
                        page.tags.includes(PageTags.HRA_FORM) ||
                        page.tags.includes(PageTags.STANDALONE_FORM) ||
                        page.tags.includes(PageTags.STANDALONE_ANCILLARY_FORM)
                    },
                    resolve: {
                      [APP_SESSION_DATA]: applicationSessionResolver,
                      [ENROLLMENT_TRANSACTION_ID]: this.getFormResolver(page.tags),
                      [LOGIN_STATUS]: loginStatusResolver,
                      [SET_SHARED_RESOURCES]: sharedResourcesResolver,
                      [QUOTE_FETCH]: quoteResolver
                    }
                  }
                );
              }
            } else {
              const pageTemplate = TemplateLookupService.getTemplate(page.template);
              if (pageTemplate) {
                pages.push({
                  matcher: caseInsensitiveMatcher(page.pageName),
                  component: pageTemplate,
                  pathMatch: 'full',
                  data: { title: page.title },
                  canDeactivate: [widgetValidationGuard, canDeactivateGuard, apiTokenGuard],
                  canActivate: this.getActivationGuards(page),
                  resolve: {
                    [GOOGLE_TAG_MANAGER]: googleTagManagerResolver,
                    [SET_SITE_TYPE]: siteTypeResolver,
                    [APP_SESSION_DATA]: applicationSessionResolver,
                    [LOGIN_STATUS]: loginStatusResolver,
                    [ACCOUNT_OVERVIEW]: accountOverviewResolver,
                    [SET_SHARED_RESOURCES]: sharedResourcesResolver,
                    [RETIRE_FLO_SURVEY]: retireFloSurveyResolver,
                    [QUOTE_FETCH]: quoteResolver
                  }
                });
              } else if (page.redirectTo) {
                pages.push({
                  matcher: caseInsensitiveMatcher(page.pageName),
                  redirectTo: page.redirectTo,
                  pathMatch: 'full',
                  resolve: {
                    [SET_SITE_TYPE]: siteTypeResolver,
                    [APP_SESSION_DATA]: applicationSessionResolver,
                    [LOGIN_STATUS]: loginStatusResolver,
                    [SET_SHARED_RESOURCES]: sharedResourcesResolver,
                    [RETIRE_FLO_SURVEY]: retireFloSurveyResolver,
                    [QUOTE_FETCH]: quoteResolver
                  }
                });
              }
            }
          }
          return pages;
        }, []);
    }

    // redirect to homepage when we hit root path
    if (config.homepage) {
      routes.unshift({
        path: '',
        redirectTo: config.homepage,
        pathMatch: 'full',
        resolve: {
          [APP_SESSION_DATA]: applicationSessionResolver,
          [LOGIN_STATUS]: loginStatusResolver
        }
      });
    }

    if (environment.liveedit) {
      const liveEditRoot = RouteBuilder.buildLiveEditRoot();
      liveEditRoot.children?.unshift(...routes);
      return [liveEditRoot];
    }
    return routes;
  }

  static getFormResolver(pageTags: string[]): any {
    if (pageTags.includes(PageTags.HRA_FORM)) {
      return hraFormTransactionResolver;
    } else if (pageTags.includes(PageTags.STANDALONE_FORM) || pageTags.includes(PageTags.STANDALONE_ANCILLARY_FORM)) {
      return standaloneFormTransactionResolver;
    } else if (pageTags.includes(PageTags.GENERIC_FORM) || pageTags.includes(PageTags.ANCILLARY_FORM)) {
      return genericFormTransactionResolver;
    } else {
      return enrollmentTransactionResolver;
    }
  }

  static includesFormPageTag(pageTags: string[]): boolean {
    return (
      pageTags.includes(PageTags.ENROLLMENT) ||
      pageTags.includes(PageTags.GENERIC_FORM) ||
      pageTags.includes(PageTags.STANDALONE_FORM) ||
      pageTags.includes(PageTags.STANDALONE_ANCILLARY_FORM) ||
      pageTags.includes(PageTags.HRA_FORM) ||
      pageTags.includes(PageTags.ANCILLARY_FORM)
    );
  }

  static buildLiveEditRoot(): Route {
    return {
      matcher: caseInsensitiveMatcher(LIVE_EDIT_ROOT),
      component: LiveEditRootComponent,
      children: [{ path: '**', component: LiveEditRootComponent }],
      resolve: {
        [API_AUTH_STATUS]: apiAuthResolver
      }
    };
  }
}
